import Fuse from "fuse.js";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { DocumentsIcon } from "@transfr-inc/dashboard-components";
import { SearchBox, useSearch } from "@transfr-inc/dashboard-components/forms";

import { SearchboxMethod, SearchEvent } from "@views/curriculum-resources/tracking";

import "./documents.searchbox.scss";

const searchKeysMap = {
  activityName: "activity type",
  description: "description",
  name: "name",
  simulations: "simulation",
  durationMinutes: "estimated time",
  industries: "industry",
  type: "type",
};

export const DocumentsSearchbox = ({
  data,
  defaultTerm,
  filters,
  onSearchTermChange,
}) => {
  const fuse = useMemo(() => {
    const fuseOptions = {
      // To display the matches in the search results
      includeMatches: true,
      // Get results closer to a perfect match
      threshold: 0.3,
      // To allow advanced search using query syntax
      useExtendedSearch: true,
      // Name is the most important field to search for
      keys: [
        { name: "name", weight: 2 },
        "description",
        "activityName",
        "simulations",
        "industries",
        "durationMinutes",
        "fileType",
        "fileIcon",
      ],
    };

    const fuseIndex = Fuse.createIndex(fuseOptions.keys, data);
    return new Fuse(data, fuseOptions, fuseIndex);
  }, [data]);

  const search = useSearch({
    data,
    queryFn: (_, term) => {
      const { term: searchTerm, limit } =
        typeof term === "string" ? { term, limit: 5 } : term;
      // Tokenize the term to give priority to exact matches
      const tokenizedTerm = `="${searchTerm}"|"${searchTerm}"`;
      // Add filters to the query only if they are present
      const andConditions = [];
      const orConditions = [
        { name: tokenizedTerm },
        { description: tokenizedTerm },
        { fileType: tokenizedTerm },
      ];

      // Add activity filter to the query if present
      if (filters.activity) {
        andConditions.push({ activityName: filters.activity.name });
      } else {
        orConditions.push({ activityName: tokenizedTerm });
      }

      // Add simulation filter to the query if present
      if (filters.simulation) {
        andConditions.push({ simulations: filters.simulation });
      } else {
        orConditions.push({ simulations: tokenizedTerm });
      }

      // Add industry filter to the query if present
      if (filters.industry) {
        andConditions.push({ industries: filters.industry });
      } else {
        orConditions.push({ industries: tokenizedTerm });
      }

      // Add estimated time filter to the query if present
      if (filters.estimatedTime) {
        andConditions.push({ durationMinutes: filters.estimatedTime });
      }

      const query = {
        $and: [
          ...andConditions,
          {
            $or: orConditions,
          },
        ],
      };

      const results = searchTerm ? fuse.search(query, { limit }) : undefined;

      // Remove the name from the matches
      results?.forEach((r) => {
        r.matches = r.matches.filter((m) => m.key !== searchKeysMap.name);
      });

      return results;
    },
  });

  const onResultSelectedHandler = ({ item }) => {
    const { name, id } = item;

    onSearchTermChange({
      event: new SearchEvent(SearchboxMethod.RESULT_SELECTED, name, name),
      data: {
        resourceIds: [id],
        term: name,
      },
    });
  };

  const onSearch = (term) => {
    const results = search.queryData({ term, limit: undefined });
    onSearchTermChange({
      event: new SearchEvent(SearchboxMethod.KEY_PRESS, term),
      data: {
        resourceIds: results?.map((r) => r.item.id),
        term,
      },
    });
  };

  const onSearchTermChangeHandler = (term) => {
    // Workaround to clear the search box. When the user clicks clear
    // button the onSearchTermChange is called with an empty string.
    // With first validation, we prevent to display results without user action.
    // With second validation, we ensure to trigger the search when default term is provided.
    if (!term && term !== defaultTerm) {
      onSearch(term);
    }
  };

  return (
    <SearchBox
      className="documents-searchbox"
      customOptionLabel={(r) => r.item.name}
      optionComponent={(r) => (
        <div className="documents-searchbox-option">
          <DocumentsIcon mimeType={r.item?.mimeType} />
          <div className="documents-searchbox-option-content">
            <div className="documents-searchbox-option-name">
              {r.item?.name}
            </div>
            {r.matches?.length > 0 && (
              <div className="documents-searchbox-option-matches">
                {r.matches?.map((m) => (
                  <span key={m.key}>
                    {searchKeysMap[m.key]}: {m.value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      data={data}
      onResultSelected={onResultSelectedHandler}
      query={search.queryData}
      placeholder="Search by title, description, category or simulation"
      onSearch={onSearch}
      onSearchTermChange={onSearchTermChangeHandler}
      searchTerm={defaultTerm}
    />
  );
};

DocumentsSearchbox.propTypes = {
  data: PropTypes.array.isRequired,
  filters: PropTypes.object,
  onSearchTermChange: PropTypes.func.isRequired,
};
